import type { ChainId, TokenSymbol } from '@cryptopay/core';

export interface ApiClientOptions {
  readonly baseUrl: string;
  readonly apiKey: string;
  readonly fetcher?: typeof fetch;
}

export interface UpsertUserResponse {
  readonly userId: string;
  readonly email: string;
  readonly address: string;
  readonly merchantId: string;
}

export interface OnRampSessionRequest {
  readonly chainId: ChainId;
  readonly cryptoCurrencyCode: TokenSymbol;
  readonly walletAddress: string;
  readonly fiatCurrency: string;
  readonly fiatAmount?: number;
}

export interface OnRampSessionResponse {
  readonly url: string;
  readonly partnerOrderId: string;
  readonly expiresAt: string;
}

export interface RelayTransferRequest {
  readonly chainId: ChainId;
  readonly signedTx: string;
}

export interface RelayTransferResponse {
  readonly merchantId: string;
  readonly chainId: ChainId;
  readonly token: { symbol: string; address: string; decimals: number };
  readonly from: string;
  readonly recipient: string;
  readonly amount: string;
  readonly amountFormatted: string;
  readonly broadcast: { hash: string; broadcastAt: string };
}

export interface HealthResponse {
  readonly status: string;
  readonly services: Readonly<Record<string, boolean>>;
}

export interface PublicMerchant {
  readonly merchantId: string;
  readonly name: string;
  readonly masterWallets: Partial<Record<ChainId, string>>;
  readonly webhookUrl: string | null;
  readonly allowedOrigins: readonly string[];
  readonly status: string;
}

export interface GetMerchantResponse {
  readonly merchant: PublicMerchant;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class ApiClient {
  private readonly fetcher: typeof fetch;

  constructor(private readonly options: ApiClientOptions) {
    // Permit an empty baseUrl at construction time so the widget can
    // still render (and the DebugBanner can surface the issue), but
    // throw a clear error the moment any RPC is attempted.
    this.fetcher = options.fetcher ?? fetch.bind(globalThis);
  }

  async health(): Promise<HealthResponse> {
    return this.send<HealthResponse>('GET', '/v1/health');
  }

  async upsertUser(idToken: string): Promise<UpsertUserResponse> {
    return this.send<UpsertUserResponse>('POST', '/v1/users/upsert', { idToken });
  }

  async createOnRampSession(req: OnRampSessionRequest): Promise<OnRampSessionResponse> {
    return this.send<OnRampSessionResponse>('POST', '/v1/onramp/sessions', req);
  }

  async relayTransfer(req: RelayTransferRequest): Promise<RelayTransferResponse> {
    return this.send<RelayTransferResponse>('POST', '/v1/transfers/relay', req);
  }

  async getMerchant(): Promise<GetMerchantResponse> {
    return this.send<GetMerchantResponse>('GET', '/v1/merchants/me');
  }

  private async send<T>(method: string, path: string, body?: unknown): Promise<T> {
    if (!this.options.baseUrl) {
      throw new ApiError(
        'API base URL is not configured. Set VITE_API_BASE_URL (e.g. https://api.example.com) in the Vercel project Environment Variables and redeploy.',
        0,
        'NO_BASE_URL',
      );
    }
    // Path always starts with `/v1/...`. We accept two shapes of baseUrl:
    //   - "https://host"        → final URL is `https://host/v1/…`
    //   - "https://host/v1"     → strip the redundant suffix, same final URL
    // The earlier version stripped `/v1$` unconditionally, which on a
    // relative dev fallback like "/v1" collapsed to "" and silently
    // pointed every request at the widget's own Vercel host (→ 405).
    const trimmed = this.options.baseUrl.replace(/\/+$/, '');
    const isAbsolute = /^https?:\/\//i.test(trimmed);
    const baseUrl = isAbsolute ? trimmed.replace(/\/v1$/i, '') : trimmed;
    const url = `${baseUrl}${path}`;
    const headers: Record<string, string> = {
      'content-type': 'application/json',
      accept: 'application/json',
      'x-api-key': this.options.apiKey,
    };
    const init: RequestInit = { method, headers };
    if (body !== undefined) init.body = JSON.stringify(body);
    const res = await this.fetcher(url, init);
    const text = await res.text();
    let parsed: unknown;
    try {
      parsed = text.length > 0 ? JSON.parse(text) : null;
    } catch {
      throw new ApiError(`Invalid JSON response from ${path}`, res.status, 'INVALID_RESPONSE');
    }
    if (!res.ok) {
      const errorBody = isObject(parsed) && isObject(parsed.error) ? parsed.error : null;
      throw new ApiError(
        (errorBody?.message as string) ?? `Request failed: ${res.status}`,
        res.status,
        (errorBody?.code as string) ?? 'REQUEST_FAILED',
      );
    }
    if (isObject(parsed) && 'data' in parsed) {
      return parsed.data as T;
    }
    return parsed as T;
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
